import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import {
  Container,
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Input,
  Label,
  Badge,
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// PrimeReact
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

// Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";

// i18n
import { withTranslation } from "react-i18next";
import { apiDelete, apiGet, apiPost, apiPut } from "../../helpers/apiService";
import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import ActionEdit from "components/buttons/actionEdit";
import ActionDelete from "components/buttons/actionDelete";
import EmailContentEditor from "components/Common/ContentEditor";
import RsLink from "components/buttons/rsLink";

const DEFAULT_FORM = {
  planCode: "",
  name: "",
  versionNo: 1,
  planDescription: "",
  planIncludes: "",
  versionDescription: "",
  versionIncludes: "",
  users: "",
  features: [""],
  prices: [
    {
      billingInterval: "MONTHLY",
      currencyCode: "USD",
      amount: "",
      priceType: "STANDARD",
      description: "",
      isDefault: true,
      isActive: true,
    },
    {
      billingInterval: "MONTHLY",
      currencyCode: "CAD",
      amount: "",
      priceType: "STANDARD",
      description: "",
      isDefault: false,
      isActive: true,
    },
    {
      billingInterval: "ANNUAL",
      currencyCode: "USD",
      amount: "",
      priceType: "STANDARD",
      description: "",
      isDefault: true,
      isActive: true,
    },
    {
      billingInterval: "ANNUAL",
      currencyCode: "CAD",
      amount: "",
      priceType: "STANDARD",
      description: "",
      isDefault: false,
      isActive: true,
    },
  ],
  status: "Active",
};

const REQUIRED_PRICE_COMBINATIONS = [
  { billingInterval: "MONTHLY", currencyCode: "USD" },
  { billingInterval: "MONTHLY", currencyCode: "CAD" },
  { billingInterval: "ANNUAL", currencyCode: "USD" },
  { billingInterval: "ANNUAL", currencyCode: "CAD" },
];

const subscriptionPlanValidationSchema = Yup.object({
  planCode: Yup.string().trim().required("Plan Code is required"),
  name: Yup.string().trim().required("Plan Name is required"),
  versionNo: Yup.number()
    .typeError("Version No is required")
    .required("Version No is required")
    .min(1, "Version No must be at least 1"),
  planDescription: Yup.string().trim().required("Plan Description is required"),
  planIncludes: Yup.string().trim().required("Plan Includes is required"),
  versionDescription: Yup.string()
    .trim()
    .required("Version Description is required"),
  versionIncludes: Yup.string().trim().required("Version Includes is required"),
  users: Yup.number()
    .typeError("Allowed Users is required")
    .required("Allowed Users is required")
    .min(1, "Allowed Users must be at least 1"),
  status: Yup.string().trim().required("Status is required"),
  prices: Yup.array()
    .of(
      Yup.object({
        billingInterval: Yup.string().trim().required("Interval is required"),
        currencyCode: Yup.string().trim().required("Currency is required"),
        amount: Yup.number()
          .typeError("Amount is required")
          .required("Amount is required")
          .moreThan(0, "Amount must be greater than 0"),
        priceType: Yup.string().trim().required("Type is required"),
      })
    )
    .min(1, "At least one price is required"),
});

const AdminSubscriptionPlans = props => {
  document.title = "Admin | Subscription Plans";

  // ================= STATE =================
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [editPlan, setEditPlan] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [isVersionViewMode, setIsVersionViewMode] = useState(false);
  const [isVersionDetailsMode, setIsVersionDetailsMode] = useState(false);
  const [isNewVersionMode, setIsNewVersionMode] = useState(false);

  const [plans, setPlans] = useState([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [expandedRows, setExpandedRows] = useState(null);
  const [currentVersionId, setCurrentVersionId] = useState(null);

  const [formInitialValues, setFormInitialValues] = useState(DEFAULT_FORM);

  // ================= HANDLERS =================
  const toggleOffcanvas = () => {
    setShowOffcanvas(!showOffcanvas);
    setIsViewMode(false);
    setIsVersionViewMode(false);
    setIsVersionDetailsMode(false);
    setIsNewVersionMode(false);
    setCurrentVersionId(null);
    if (!showOffcanvas) {
      setEditPlan(null);
      setFormInitialValues(DEFAULT_FORM);
    }
  };

  const normalizePlanRow = (plan, index) => {
    const versions = Array.isArray(plan?.versions) ? plan.versions : [];
    const latestVersion = versions.length
      ? [...versions].sort(
          (a, b) =>
            Number(b?.versionNo || 0) - Number(a?.versionNo || 0) ||
            Number(b?.id || 0) - Number(a?.id || 0)
        )[0]
      : null;
    const prices = Array.isArray(latestVersion?.prices) ? latestVersion.prices : [];
    const monthlyPrice = prices.find(
      p => String(p?.billingInterval || "").toLowerCase() === "monthly"
    );
    const annualPrice = prices.find(
      p => String(p?.billingInterval || "").toLowerCase() === "annual"
    );

    const parsedPlanIncludes =
      typeof plan?.planIncludes === "string"
        ? plan.planIncludes
            .split(",")
            .map(item => item.trim())
            .filter(Boolean)
        : [];
    const parsedVersionIncludes =
      typeof latestVersion?.planIncludes === "string"
        ? latestVersion.planIncludes
            .split(",")
            .map(item => item.trim())
            .filter(Boolean)
        : typeof plan?.versionIncludes === "string"
          ? plan.versionIncludes
            .split(",")
            .map(item => item.trim())
            .filter(Boolean)
          : [];
    const features = [...parsedPlanIncludes, ...parsedVersionIncludes];
    const normalizedVersions = versions.map(version => ({
      id: version?.id,
      versionNo: version?.versionNo ?? "-",
      description: version?.description || "-",
      planIncludes: version?.planIncludes || "-",
      isVisible: version?.isVisible ? "Yes" : "No",
      status:
        version?.isActiveForNewUsers || version?.isActiveForExistingSubscribers
          ? "Active"
          : "Inactive",
      prices: Array.isArray(version?.prices)
        ? version.prices.map(price => ({
            billingInterval: String(price?.billingInterval || "").toUpperCase(),
            currencyCode: String(price?.currencyCode || "USD").toUpperCase(),
            amount: Number(price?.amount || 0).toFixed(2),
          }))
        : [],
      rawPrices: Array.isArray(version?.prices)
        ? version.prices.map(price => ({
            billingInterval: String(price?.billingInterval || "MONTHLY").toUpperCase(),
            currencyCode: String(price?.currencyCode || "USD").toUpperCase(),
            amount: price?.amount ?? "",
            priceType: String(price?.priceType || "STANDARD").toUpperCase(),
            description: price?.description || "",
            isDefault: Boolean(price?.isDefault),
            isActive: price?.isActive !== false,
          }))
        : [],
    }));

    return {
      id: plan?.subscriptionPlanId || plan?.id || index + 1,
      planCode: plan?.planCode || "",
      name: plan?.name || "",
      versionNo: latestVersion?.versionNo ?? plan?.versionNo ?? 1,
      planDescription: plan?.description || plan?.planDescription || "",
      planIncludes:
        typeof plan?.planIncludes === "string" ? plan.planIncludes : "",
      versionDescription: latestVersion?.description || plan?.versionDescription || "",
      versionIncludes:
        typeof latestVersion?.planIncludes === "string"
          ? latestVersion.planIncludes
          : typeof plan?.versionIncludes === "string"
            ? plan.versionIncludes
            : "",
      users: "",
      features,
      versions: normalizedVersions,
      prices: prices.map(price => ({
        billingInterval: String(price?.billingInterval || "MONTHLY").toUpperCase(),
        currencyCode: price?.currencyCode || "USD",
        amount: price?.amount ?? "",
        priceType: String(price?.priceType || "STANDARD").toUpperCase(),
        description: price?.description || "",
        isDefault: Boolean(price?.isDefault),
        isActive: price?.isActive !== false,
      })),
      status:
        latestVersion?.isActiveForNewUsers ||
        latestVersion?.isActiveForExistingSubscribers ||
        plan?.isActiveForNewUsers ||
        plan?.isActiveForExistingSubscribers
          ? "Active"
          : "Inactive",
    };
  };

  const fetchAllPlans = async () => {
    try {
      setIsLoadingPlans(true);
      const response = await apiGet("admin/subscriptionplans/all-with-versions-prices");
      const data = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.resultData)
          ? response.data.resultData
          : [];
      setPlans(data.map((plan, index) => normalizePlanRow(plan, index)));
    } catch (error) {
      toast.error(
        error?.response?.data?.validationErrors?.[0] ||
          error?.response?.data?.message ||
          "Failed to load subscription plans"
      );
    } finally {
      setIsLoadingPlans(false);
    }
  };

  useEffect(() => {
    fetchAllPlans();
  }, []);

  const buildCreatePayload = values => {
    const nowIso = new Date().toISOString();
    const cleanFeatures = Array.isArray(values.features)
      ? values.features.map(item => item?.trim()).filter(Boolean)
      : [];
    const payloadPrices = Array.isArray(values.prices)
      ? values.prices.map(price => ({
          billingInterval: String(price?.billingInterval || "MONTHLY").toUpperCase(),
          currencyCode: String(price?.currencyCode || "USD").toUpperCase(),
          amount: Number(price?.amount) || 0,
          priceType: String(price?.priceType || "STANDARD").toUpperCase(),
          description: price?.description || "",
          isDefault: price?.isDefault !== false,
          effectiveFromUtc: nowIso,
          effectiveToUtc: null,
          isActive: values.status === "Active" && price?.isActive !== false,
        }))
      : [];

    return {
      planCode:
        values.planCode ||
        values.name?.trim()?.toUpperCase().replace(/\s+/g, "-") ||
        "",
      name: values.name?.trim() || "",
      versionNo: Number(values.versionNo) || 1,
      isVisible: true,
      isActiveForNewUsers: values.status === "Active",
      isActiveForExistingSubscribers: values.status === "Active",
      planDescription: values.planDescription || "",
      planIncludes:
        values.planIncludes ||
        cleanFeatures.join(", ") ||
        `Up to ${Number(values.users) || 0} users`,
      versionDescription: values.versionDescription || "",
      versionIncludes: values.versionIncludes || cleanFeatures.join(", "),
      prices: payloadPrices,
    };
  };

  const savePlan = async values => {
    if (editPlan) {
      if (currentVersionId) {
        const nowIso = new Date().toISOString();
        const payload = {
          versionNo: Number(values?.versionNo) || 0,
          isVisible: true,
          isActiveForNewUsers:
            values?.status ? values.status === "Active" : true,
          isActiveForExistingSubscribers:
            values?.status ? values.status === "Active" : true,
          effectiveFromUtc: nowIso,
          effectiveToUtc: nowIso,
          description: values?.versionDescription || "string",
          planIncludes: values?.versionIncludes || "string",
        };

        try {
          setIsSaving(true);
          await apiPut(`admin/subscriptionplanversions/${currentVersionId}`, payload);
          await fetchAllPlans();
          toast.success("Version updated successfully");
          toggleOffcanvas();
        } catch (error) {
          toast.error(
            error?.response?.data?.validationErrors?.[0] ||
              error?.response?.data?.message ||
              "Failed to update version"
          );
        } finally {
          setIsSaving(false);
        }
      } else {
        setPlans(plans.map(p =>
          p.id === editPlan.id ? { ...p, ...values } : p
        ));
        toggleOffcanvas();
      }
      return;
    }

    const missingRequiredPrices = REQUIRED_PRICE_COMBINATIONS.filter(requiredPrice => {
      const matchingPrice = values.prices.find(
        price =>
          String(price?.billingInterval || "").toUpperCase() ===
            requiredPrice.billingInterval &&
          String(price?.currencyCode || "").toUpperCase() ===
            requiredPrice.currencyCode &&
          Number(price?.amount) > 0
      );
      return !matchingPrice;
    });

    if (missingRequiredPrices.length) {
      const missingText = missingRequiredPrices
        .map(item => `${item.billingInterval}-${item.currencyCode}`)
        .join(", ");
      toast.error(`Required prices missing: ${missingText}`);
      return;
    }

    try {
      setIsSaving(true);
      await apiPost("admin/subscriptionplans", buildCreatePayload(values));
      await fetchAllPlans();
      toast.success("Subscription plan added");
      toggleOffcanvas();
    } catch (error) {
      toast.error(
        error?.response?.data?.validationErrors?.[0] ||
          error?.response?.data?.message ||
          "Failed to add subscription plan"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const deleteHandler = async row => {
    if (!row?.id) {
      toast.error("Invalid plan selected");
      return;
    }

    try {
      await apiDelete(`admin/subscriptionplans/${row.id}`);
      setPlans(prevPlans => prevPlans.filter(plan => plan.id !== row.id));
      toast.success("Subscription plan deleted");
    } catch (error) {
      toast.error(
        error?.response?.data?.validationErrors?.[0] ||
          error?.response?.data?.message ||
          "Failed to delete subscription plan"
      );
    }
  };

  const applyRowToForm = row => {
    setIsViewMode(false);
    setIsVersionViewMode(false);
    setIsVersionDetailsMode(false);
    setIsNewVersionMode(false);
    setCurrentVersionId(null);
    setEditPlan(row);
    setFormInitialValues({
      ...DEFAULT_FORM,
      ...row,
      features:
        Array.isArray(row.features) && row.features.length ? row.features : [""],
      prices:
        Array.isArray(row.prices) && row.prices.length
          ? row.prices
          : [
              {
                billingInterval: "MONTHLY",
                currencyCode: "USD",
                amount: row.monthlyPrice ?? row.price ?? "",
                priceType: "STANDARD",
                description: "",
                isDefault: true,
                isActive: row.status === "Active",
              },
              {
                billingInterval: "ANNUAL",
                currencyCode: "USD",
                amount: row.annualPrice ?? "",
                priceType: "STANDARD",
                description: "",
                isDefault: true,
                isActive: row.status === "Active",
              },
            ],
    });
    setShowOffcanvas(true);
  };

  const editHandler = row => {
    applyRowToForm(row);
  };

  const viewHandler = async row => {
    if (!row?.id) {
      toast.error("Invalid plan selected");
      return;
    }

    try {
      const response = await apiGet(`admin/subscriptionplans/${row.id}/versions`);
      const detail =
        response?.data?.resultData ||
        response?.data?.data ||
        response?.data ||
        row;

      if (Array.isArray(detail) && detail.length) {
        const sorted = [...detail].sort(
          (a, b) => Number(b?.versionNo || 0) - Number(a?.versionNo || 0)
        );
        const viewRecord = sorted[0];
        const mappedPrices =
          Array.isArray(viewRecord?.prices) && viewRecord.prices.length
            ? viewRecord.prices.map(price => ({
                billingInterval: String(price?.billingInterval || "MONTHLY").toUpperCase(),
                currencyCode: String(price?.currencyCode || "USD").toUpperCase(),
                amount: price?.amount ?? "",
                priceType: String(price?.priceType || "STANDARD").toUpperCase(),
                description: price?.description || "",
                isDefault: Boolean(price?.isDefault),
                isActive: price?.isActive !== false,
              }))
            : DEFAULT_FORM.prices;

        setEditPlan({ id: viewRecord?.subscriptionPlanId || row?.id });
        setFormInitialValues({
          ...DEFAULT_FORM,
          planCode: viewRecord?.planCode || "",
          name: viewRecord?.name || "",
          versionNo: viewRecord?.versionNo ?? 1,
          planDescription:
            viewRecord?.planDescription || viewRecord?.description || "",
          planIncludes:
            typeof viewRecord?.planIncludes === "string"
              ? viewRecord.planIncludes
              : "",
          versionDescription:
            viewRecord?.versionDescription || viewRecord?.description || "",
          versionIncludes:
            typeof viewRecord?.versionIncludes === "string"
              ? viewRecord.versionIncludes
              : "",
          status:
            viewRecord?.isActiveForNewUsers ||
            viewRecord?.isActiveForExistingSubscribers
              ? "Active"
              : "Inactive",
          prices: mappedPrices,
        });
        setIsViewMode(false);
        setShowOffcanvas(true);
      } else {
        const normalizedRow = normalizePlanRow(detail, 0);
        applyRowToForm(normalizedRow);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.validationErrors?.[0] ||
          error?.response?.data?.message ||
          "Failed to load plan details"
      );
    }
  };


  const newVersionHandler = async row => {
    if (!row?.id) {
      toast.error("Invalid plan selected");
      return;
    }

    try {
      const response = await apiGet(`admin/subscriptionplans/${row.id}/versions`);
      const detail =
        response?.data?.resultData ||
        response?.data?.data ||
        response?.data ||
        row;
      const sourceRecord = Array.isArray(detail) ? detail[0] || {} : detail || {};
      const mappedPrices =
        Array.isArray(sourceRecord?.prices) && sourceRecord.prices.length
          ? sourceRecord.prices.map(price => ({
              billingInterval: String(price?.billingInterval || "MONTHLY").toUpperCase(),
              currencyCode: String(price?.currencyCode || "USD").toUpperCase(),
              amount: price?.amount ?? "",
              priceType: String(price?.priceType || "STANDARD").toUpperCase(),
              description: price?.description || "",
              isDefault: Boolean(price?.isDefault),
              isActive: price?.isActive !== false,
            }))
          : DEFAULT_FORM.prices;

      setIsViewMode(false);
      setIsVersionViewMode(false);
      setIsVersionDetailsMode(false);
      setIsNewVersionMode(true);
      setCurrentVersionId(null);
      setEditPlan({ id: sourceRecord?.subscriptionPlanId || row?.id });
      setFormInitialValues({
        ...DEFAULT_FORM,
        planCode: sourceRecord?.planCode || row?.planCode || "",
        name: sourceRecord?.name || row?.name || "",
        planDescription:
          sourceRecord?.planDescription || sourceRecord?.description || "",
        planIncludes:
          typeof sourceRecord?.planIncludes === "string"
            ? sourceRecord.planIncludes
            : "",
        versionNo: Number(sourceRecord?.versionNo || 0) + 1,
        versionDescription:
          sourceRecord?.versionDescription || sourceRecord?.description || "",
        versionIncludes:
          typeof sourceRecord?.versionIncludes === "string"
            ? sourceRecord.versionIncludes
            : "",
        status:
          sourceRecord?.isActiveForNewUsers ||
          sourceRecord?.isActiveForExistingSubscribers
            ? "Active"
            : "Inactive",
        prices: mappedPrices,
      });
      setShowOffcanvas(true);
    } catch (error) {
      toast.error(
        error?.response?.data?.validationErrors?.[0] ||
          error?.response?.data?.message ||
          "Failed to load plan details"
      );
    }
  };


  const addView = async row => {
    if (!row?.id) {
      toast.error("Invalid plan selected");
      return;
    }

    try {
      const response = await apiGet(`admin/subscriptionplans/${row.id}/versions`);
      const detail =
        response?.data?.resultData ||
        response?.data?.data ||
        response?.data ||
        row;
      const normalizedRow = normalizePlanRow(detail, 0);
      applyRowToForm(normalizedRow);
    } catch (error) {
      toast.error(
        error?.response?.data?.validationErrors?.[0] ||
          error?.response?.data?.message ||
          "Failed to load plan details"
      );
    }
  };

  const openVersionInForm = (planRow, version, appendPriceRow = false) => {
    const basePrices =
      Array.isArray(version?.rawPrices) && version.rawPrices.length
        ? version.rawPrices
        : DEFAULT_FORM.prices;
    const prices = appendPriceRow
      ? [
          ...basePrices,
          {
            billingInterval: "MONTHLY",
            currencyCode: "USD",
            amount: "",
            priceType: "STANDARD",
            description: "",
            isDefault: false,
            isActive: true,
          },
        ]
      : basePrices;

    setCurrentVersionId(version?.id || null);
    setEditPlan(planRow);
    setFormInitialValues({
      ...DEFAULT_FORM,
      ...planRow,
      versionNo: version?.versionNo ?? planRow?.versionNo ?? 1,
      versionDescription:
        version?.description && version.description !== "-"
          ? version.description
          : "",
      versionIncludes:
        version?.planIncludes && version.planIncludes !== "-"
          ? version.planIncludes
          : "",
      prices,
    });
    setShowOffcanvas(true);
  };

  const viewVersionHandler = async (planRow, version) => {
    if (!version?.id) {
      toast.error("Invalid version selected");
      return;
    }

    try {
      const response = await apiGet(`admin/subscriptionplanversions/${version.id}`);

      const resultData =
        response?.data?.resultData || response?.data?.data || response?.data || {};
      const detail = Array.isArray(resultData) ? resultData[0] || {} : resultData;

      const mappedVersion = {
        ...version,
        versionNo: detail?.versionNo ?? version?.versionNo ?? 1,
        description: detail?.description || detail?.versionDescription || "",
        planIncludes: detail?.planIncludes || detail?.versionIncludes || "",
        rawPrices:
          Array.isArray(detail?.prices) && detail.prices.length
            ? detail.prices.map(price => ({
                billingInterval: String(price?.billingInterval || "MONTHLY").toUpperCase(),
                currencyCode: String(price?.currencyCode || "USD").toUpperCase(),
                amount: price?.amount ?? "",
                priceType: String(price?.priceType || "STANDARD").toUpperCase(),
                description: price?.description || "",
                isDefault: Boolean(price?.isDefault),
                isActive: price?.isActive !== false,
              }))
            : version?.rawPrices || [],
      };

      setIsVersionViewMode(false);
      setIsVersionDetailsMode(true);
      openVersionInForm(planRow, mappedVersion, false);
    } catch (error) {
      toast.error(
        error?.response?.data?.validationErrors?.[0] ||
          error?.response?.data?.message ||
          "Failed to load version details"
      );
    }
  };

  const addVersionPriceHandler = async (planRow, version) => {
    if (!version?.id) {
      toast.error("Invalid version selected");
      return;
    }

    try {
      const response = await apiGet(`admin/subscriptionplanversions/${version.id}`);
      const resultData =
        response?.data?.resultData || response?.data?.data || response?.data || {};
      const detail = Array.isArray(resultData) ? resultData[0] || {} : resultData;

      const mappedVersion = {
        ...version,
        versionNo: detail?.versionNo ?? version?.versionNo ?? 1,
        description: detail?.description || detail?.versionDescription || "",
        planIncludes: detail?.planIncludes || detail?.versionIncludes || "",
        rawPrices:
          Array.isArray(detail?.prices) && detail.prices.length
            ? detail.prices.map(price => ({
                billingInterval: String(price?.billingInterval || "MONTHLY").toUpperCase(),
                currencyCode: String(price?.currencyCode || "USD").toUpperCase(),
                amount: price?.amount ?? "",
                priceType: String(price?.priceType || "STANDARD").toUpperCase(),
                description: price?.description || "",
                isDefault: Boolean(price?.isDefault),
                isActive: price?.isActive !== false,
              }))
            : version?.rawPrices || [],
      };

      setIsVersionViewMode(true);
      setIsVersionDetailsMode(false);
      openVersionInForm(planRow, mappedVersion, true);
    } catch (error) {
      toast.error(
        error?.response?.data?.validationErrors?.[0] ||
          error?.response?.data?.message ||
          "Failed to load version details"
      );
    }
  };

  // ================= TEMPLATES =================
  const statusTemplate = row => (
    <Badge color={row.status === "Active" ? "success" : "secondary"}>
      {row.status}
    </Badge>
  );

  const actionTemplate = row => (
    <div className="d-flex gap-2">
      <RsLink className="btn btn-view" iconClass="" to="#" onClick={() => viewHandler(row)}>View</RsLink>
      <RsLink className="btn btn-new-version" iconClass="" to="#" onClick={() => newVersionHandler(row)}>New version</RsLink>
   
      <RsLink className="btn btn-audit" iconClass="" to="#">Audit</RsLink>
      <RsLink className="btn btn-delete" iconClass="" to="#">Delete</RsLink>
      
    </div>
   
  );

  const rowExpansionTemplate = row => (
    <div className="p-3">
      <h6 className="mb-3">Versions</h6>
      <DataTable
        value={Array.isArray(row?.versions) ? row.versions : []}
        responsiveLayout="scroll"
        emptyMessage="No versions found"
      >
        <Column field="versionNo" header="Version" />
      
        <Column field="isVisible" header="Visible" />
        <Column field="status" header="Status" />
        
        <Column
          header="Action"
          body={version => (
            <div className="d-flex gap-2">
              <RsLink
                className="btn btn-view"
                iconClass=""
                to="#"
                onClick={() => viewVersionHandler(row, version)}
              >
                View
              </RsLink>
              <RsLink
                className="btn btn-add-price"
                iconClass=""
                to="#"
                onClick={() => addVersionPriceHandler(row, version)}
              >
                Add / View Price
              </RsLink>
            </div>
          )}
        />
      </DataTable>
    </div>
  );

  const priceTemplate = row => (
    <div className="d-flex flex-column gap-1">
      {(Array.isArray(row?.prices) ? row.prices : []).slice(0, 2).map((price, index) => (
        <div key={`${price?.billingInterval}-${index}`} className="d-flex align-items-center gap-2">
          <Badge color="light" className="text-dark border">
            {price?.billingInterval || "INTERVAL"}
          </Badge>
          <span className="fw-semibold">
            {String(price?.currencyCode || "USD").toUpperCase()} {Number(price?.amount || 0).toFixed(2)}
          </span>
        </div>
      ))}
    </div>
  );

  const featureTemplate = row => {
    const features = Array.isArray(row?.features)
      ? row.features
      : String(row?.featuresText || "")
          .split(",")
          .map(item => item.trim())
          .filter(Boolean);

    if (!features.length) return <span className="text-muted">-</span>;

    return (
      <div className="d-flex flex-wrap gap-1">
        {features.slice(0, 3).map((feature, index) => (
          <Badge key={`${feature}-${index}`} color="info" pill>
            {feature}
          </Badge>
        ))}
        {features.length > 3 && (
          <Badge color="secondary" pill>
            +{features.length - 3} more
          </Badge>
        )}
      </div>
    );
  };

  // ================= UI =================
  return (
    <div className="page-content">
      <ToastContainer autoClose={2000} />
      <Container fluid>

        <Breadcrumbs
          title={props.t("Admin")}
          breadcrumbItem={props.t("Subscription Plans")}
        />

        {/* ================= HEADER ================= */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="mb-0"></h4>
          <Button color="primary" onClick={toggleOffcanvas}>
            + Add Plan
          </Button>
        </div>

        {/* ================= PLANS TABLE ================= */}
        <Card>
          <CardBody>
            <DataTable
              value={plans}
              responsiveLayout="scroll"
              loading={isLoadingPlans}
              emptyMessage="No plans found"
              expandedRows={expandedRows}
              onRowToggle={e => setExpandedRows(e.data)}
              rowExpansionTemplate={rowExpansionTemplate}
              dataKey="id"
            >
              <Column expander style={{ width: "3rem" }} />
              <Column field="name" header="Plan Name" />
              <Column field="planCode" header="Plan Code" />
             
            
              
              <Column
                header="Action"
                body={actionTemplate}
              />
            </DataTable>
          </CardBody>
        </Card>

        {/* ================= ADD / EDIT OFFCANVAS ================= */}
        <Offcanvas
          isOpen={showOffcanvas}
          toggle={toggleOffcanvas}
          direction="end"
          style={{ width: "800px" }}
        >
          <OffcanvasHeader toggle={toggleOffcanvas}>
            {isViewMode ? "View Plan" : editPlan ? "Edit Plan" : "Add Plan"}
          </OffcanvasHeader>

          <OffcanvasBody>
            <Formik
              enableReinitialize
              initialValues={formInitialValues}
              validationSchema={editPlan ? undefined : subscriptionPlanValidationSchema}
              onSubmit={savePlan}
            >
              {({ values, handleChange, setFieldValue, errors, touched }) => (
                <Form>
                  <Row>
              {!isVersionViewMode && !isVersionDetailsMode && (
              <>
              <Col md="4" className="mb-3">
                <Label>Plan Code <span className="text-danger">*</span></Label>
                <Input
                  name="planCode"
                  value={values.planCode}
                  onChange={handleChange}
                  disabled={isViewMode || isNewVersionMode}
                  invalid={Boolean(touched.planCode && errors.planCode)}
                />
                {touched.planCode && errors.planCode && (
                  <div className="text-danger small mt-1">{errors.planCode}</div>
                )}
              </Col>

              <Col md="4" className="mb-3">
                <Label>Plan Name <span className="text-danger">*</span></Label>
                <Input
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  disabled={isViewMode || isNewVersionMode}
                  invalid={Boolean(touched.name && errors.name)}
                />
                {touched.name && errors.name && (
                  <div className="text-danger small mt-1">{errors.name}</div>
                )}
              </Col>

              <Col md="4" className="mb-3">
                <Label>Version No <span className="text-danger">*</span></Label>
                <Input
                  type="number"
                  name="versionNo"
                  value={values.versionNo}
                  onChange={handleChange}
                  disabled={isViewMode}
                  invalid={Boolean(touched.versionNo && errors.versionNo)}
                />
                {touched.versionNo && errors.versionNo && (
                  <div className="text-danger small mt-1">{errors.versionNo}</div>
                )}
              </Col>

              <Col md="12" className="mb-3">
                <Label>Plan Description <span className="text-danger">*</span></Label>
                <Input
                  type="textarea"
                  name="planDescription"
                  value={values.planDescription}
                  onChange={handleChange}
                  disabled={isViewMode}
                  invalid={Boolean(touched.planDescription && errors.planDescription)}
                />
                {touched.planDescription && errors.planDescription && (
                  <div className="text-danger small mt-1">{errors.planDescription}</div>
                )}
              </Col>

              <Col md="12" className="mb-3">
                <Label>Plan Includes <span className="text-danger">*</span></Label>
                {isViewMode ? (
                  <Input
                    type="textarea"
                    name="planIncludes"
                    value={
                      typeof values.planIncludes === "string"
                        ? values.planIncludes
                        : ""
                    }
                    disabled
                    style={{ minHeight: "120px" }}
                  />
                ) : (
                  <EmailContentEditor
                    initialContent={
                      typeof values.planIncludes === "string"
                        ? values.planIncludes
                        : ""
                    }
                    setFieldValue={setFieldValue}
                    name="planIncludes"
                    placeholder="Enter what the plan includes..."
                  />
                )}
                {touched.planIncludes && errors.planIncludes && (
                  <div className="text-danger small mt-1">{errors.planIncludes}</div>
                )}
          
              </Col>
              </>
              )}

              {!isVersionViewMode && !isVersionDetailsMode && (
              <Col md="12" className="mb-3">
                <Label>Version Description <span className="text-danger">*</span></Label>
                <Input
                  type="textarea"
                  name="versionDescription"
                  value={values.versionDescription}
                  onChange={handleChange}
                  disabled={isViewMode}
                  invalid={Boolean(touched.versionDescription && errors.versionDescription)}
                />
                {touched.versionDescription && errors.versionDescription && (
                  <div className="text-danger small mt-1">{errors.versionDescription}</div>
                )}
              </Col>
              )}

              {!isVersionViewMode && (
              <Col md="12" className="mb-3">
                <Label>Version Includes <span className="text-danger">*</span></Label>
                {isViewMode ? (
                  <Input
                    type="textarea"
                    name="versionIncludes"
                    value={
                      typeof values.versionIncludes === "string"
                        ? values.versionIncludes
                        : ""
                    }
                    disabled
                    style={{ minHeight: "120px" }}
                  />
                ) : (
                  <EmailContentEditor
                    initialContent={
                      typeof values.versionIncludes === "string"
                        ? values.versionIncludes
                        : ""
                    }
                    setFieldValue={setFieldValue}
                    name="versionIncludes"
                    placeholder="Enter version includes..."
                  />
                )}
                {touched.versionIncludes && errors.versionIncludes && (
                  <div className="text-danger small mt-1">{errors.versionIncludes}</div>
                )}
              </Col>
              )}

         

         

              {!isVersionViewMode && (
              <Col md="12" className="mb-3">
                <Label>Allowed Users <span className="text-danger">*</span></Label>
                <Input
                  type="number"
                  name="users"
                  value={values.users}
                  onChange={handleChange}
                  disabled={isViewMode}
                  invalid={Boolean(touched.users && errors.users)}
                />
                {touched.users && errors.users && (
                  <div className="text-danger small mt-1">{errors.users}</div>
                )}
              </Col>
              )}

              {!isVersionViewMode && !isVersionDetailsMode && (
              <Col md="12" className="mb-4">
                <Label>Status <span className="text-danger">*</span></Label>
                <Input
                  type="select"
                  name="status"
                  value={values.status}
                  onChange={handleChange}
                  disabled={isViewMode}
                  invalid={Boolean(touched.status && errors.status)}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </Input>
                {touched.status && errors.status && (
                  <div className="text-danger small mt-1">{errors.status}</div>
                )}
              </Col>
              )}

              {(!editPlan || isVersionViewMode || isNewVersionMode) && !isVersionDetailsMode && (
              <Col md="12" className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Label className="mb-0">Plan Prices</Label>
                </div>
                <FieldArray
                  name="prices"
                  render={arrayHelpers => (
                    <div className="d-flex flex-column gap-3">
                      {(() => {
                        const requiredCombinationFirstIndexMap = {};
                        REQUIRED_PRICE_COMBINATIONS.forEach(requiredPrice => {
                          const key = `${requiredPrice.billingInterval}-${requiredPrice.currencyCode}`;
                          requiredCombinationFirstIndexMap[key] = values.prices.findIndex(
                            p =>
                              String(p?.billingInterval || "").toUpperCase() ===
                                requiredPrice.billingInterval &&
                              String(p?.currencyCode || "").toUpperCase() ===
                                requiredPrice.currencyCode
                          );
                        });

                        return values.prices?.map((price, index) => {
                          const priceKey = `${String(
                            price?.billingInterval || ""
                          ).toUpperCase()}-${String(
                            price?.currencyCode || ""
                          ).toUpperCase()}`;
                          const firstRequiredIndex =
                            requiredCombinationFirstIndexMap[priceKey];
                          const isRequiredPrice =
                            firstRequiredIndex !== undefined &&
                            firstRequiredIndex !== -1 &&
                            firstRequiredIndex === index;

                        return (
                        <Card key={`price-${index}`} className="mb-0">
                          <CardBody className="py-3">
                            <Row className="g-2">
                              <Col md="3">
                                <Label>Interval <span className="text-danger">*</span></Label>
                                <Input
                                  type="select"
                                  name={`prices.${index}.billingInterval`}
                                  value={price.billingInterval}
                                  onChange={handleChange}
                                  disabled={isViewMode}
                                  invalid={Boolean(
                                    touched.prices?.[index]?.billingInterval &&
                                      errors.prices?.[index]?.billingInterval
                                  )}
                                >
                                  <option value="MONTHLY">MONTHLY</option>
                                  <option value="ANNUAL">ANNUAL</option>
                                  <option value="QUARTERLY">QUARTERLY</option>
                                  <option value="WEEKLY">WEEKLY</option>
                                </Input>
                              </Col>
                              <Col md="3">
                                <Label>Currency <span className="text-danger">*</span></Label>
                                <Input
                                  type="select"
                                  name={`prices.${index}.currencyCode`}
                                  value={price.currencyCode}
                                  onChange={handleChange}
                                  disabled={isViewMode}
                                  invalid={Boolean(
                                    touched.prices?.[index]?.currencyCode &&
                                      errors.prices?.[index]?.currencyCode
                                  )}
                                >
                                  <option value="USD">USD</option>
                                  <option value="CAD">CAD</option>
                                  <option value="EUR">EUR</option>
                                  <option value="GBP">GBP</option>
                                </Input>
                              </Col>
                              <Col md="3">
                                <Label>Amount <span className="text-danger">*</span></Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  name={`prices.${index}.amount`}
                                  value={price.amount}
                                  onChange={handleChange}
                                  disabled={isViewMode}
                                  invalid={Boolean(
                                    touched.prices?.[index]?.amount &&
                                      errors.prices?.[index]?.amount
                                  )}
                                />
                              </Col>
                              <Col md="3">
                                <Label>Type <span className="text-danger">*</span></Label>
                                <Input
                                  name={`prices.${index}.priceType`}
                                  value={price.priceType}
                                  onChange={handleChange}
                                  disabled={isViewMode}
                                  invalid={Boolean(
                                    touched.prices?.[index]?.priceType &&
                                      errors.prices?.[index]?.priceType
                                  )}
                                />
                              </Col>
                             
                              <Col md="12" className="d-flex justify-content-end gap-2">
                                <Button
                                  type="button"
                                  color="danger"
                                  outline
                                  onClick={() => {
                                    if (!isRequiredPrice && values.prices.length > 1) {
                                      arrayHelpers.remove(index);
                                    }
                                  }}
                                  disabled={isViewMode || values.prices.length <= 1 || isRequiredPrice}
                                >
                                  Remove
                                </Button>
                                <Button
                                  type="button"
                                  color="success"
                                  outline
                                  onClick={() =>
                                    arrayHelpers.insert(index + 1, {
                                      billingInterval: "MONTHLY",
                                      currencyCode: "USD",
                                      amount: "",
                                      priceType: "STANDARD",
                                      description: "",
                                      isDefault: false,
                                      isActive: true,
                                    })
                                  }
                                  disabled={isViewMode}
                                >
                                  Add More Price
                                </Button>
                              </Col>
                            </Row>
                          </CardBody>
                        </Card>
                        );
                        });
                      })()}
                    </div>
                  )}
                />
              </Col>
              )}

              <Col md="12" className="text-end">
                <Button
                  color="secondary"
                  className="me-2"
                  disabled={isSaving}
                  onClick={toggleOffcanvas}
                >
                  {isViewMode ? "Close" : "Cancel"}
                </Button>
                {!isViewMode && (
                  <Button color="primary" type="submit" disabled={isSaving}>
                    {isSaving ? "Saving..." : "Save"}
                  </Button>
                )}
              </Col>
            </Row>
                </Form>
              )}
            </Formik>
          </OffcanvasBody>
        </Offcanvas>

      </Container>
    </div>
  );
};

AdminSubscriptionPlans.propTypes = {
  t: PropTypes.any,
};

export default withTranslation()(AdminSubscriptionPlans);
